/**
 * Applicants & guarantors.
 *
 * UI RENAME ONLY (pricing-scenario-sizers.md, 2026-08-17 live-test review): the
 * section reads "Applicants & guarantors" and the button "Add applicant", but
 * the wire payload stays the canonical `borrowers[]` array — the loan model's
 * vocabulary, which Save Loan posts unchanged. The JSON field is NOT renamed.
 */
import { useEffect, useRef } from "react";
import { Field, useField } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";

import Box from "@mui/material/Box";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import {
  SZ,
  Section,
  Grid,
  Field2,
  RffText,
  RffMoney,
  RffNum,
  RffStepper,
  StepperInput,
  RffPercent,
  RffSelect,
  RffSwitch,
  RepeatItem,
  SubLabel,
  AddButton,
} from "../../sizersShared/sizerUi";
import { selectOptionsCitizenshipStatus } from "../../sizersShared/sizerSelectOptions";

// The model has no borrower "role" enum — it carries `isGuarantor` (and
// `isPrimaryContactForLoan`) as booleans, so that is what the form collects.
const roleLabel = (isGuarantor) => (isGuarantor ? "Guarantor" : "Applicant");

/**
 * The SIX experience categories the classification engine counts, not the three
 * the form used to collect. The two ground-up categories are the WHOLE of GUC
 * classification, and `income_producing_properties_purchased` /
 * `gc_for_third_party` were simply uncounted — a three-field form undercounts
 * cumulative experience and therefore resolves a lower borrower class, which
 * moves max leverage and the rate.
 *
 * The dict shape is sanctioned by the backend: `borrowers[].experience` as a
 * category-count map is summed server-side into `verifiedExperienceTotal`, so
 * this needs no rename (unlike the other sizer-form aliases).
 */
export const EXPERIENCE_CATEGORIES = [
  { key: "fix_and_flip_sold", label: "Fix & Flip sold" },
  { key: "fix_and_flip_retained_rental", label: "Fix & Flip retained as rental" },
  { key: "income_producing_properties_purchased", label: "Income Producing Properties Owned" },
  { key: "ground_up_completed_and_sold", label: "Ground-up sold" },
  { key: "ground_up_retained_as_rental", label: "Ground-up retained as rental" },
  { key: "gc_for_third_party", label: "Projects as Gen. Contractor for 3rd party" },
];

/**
 * A BLANK block. Nothing in here is a value.
 *
 * `rtl-guc-sizer-frontend.md` §2.3 is explicit about why — *"no autopopulate,
 * they will always just leave auto-populated fields"*. A defaulted FICO of 720
 * or an experience count of 0 prices the deal on a number nobody entered, and
 * prices it OPTIMISTICALLY; blank forces real entry and the missing-requirements
 * list is what says what is still owed.
 *
 * The one deliberate default is `isGuarantor` — structural (this count declares
 * guarantors), not a credit value.
 *
 * NOTE on names: `entityPercentOwnership` / `ficoMid` are the canonical LOS
 * `Borrower` fields. The build guide writes `ownershipPercent` / `bestFico` —
 * the wrapper's transitional aliases, which the backend accepts but reports back
 * in `payloadWarnings`. Canonical wins here, per the top-of-doc rule that the
 * wrapper mirrors rather than invents.
 */
export const newBorrower = ({ isGuarantor = true } = {}) => ({
  isGuarantor,
  firstName: "",
  lastName: "",
});

// Live sub-title on the repeater header (the applicant's role).
const RoleSub = ({ name }) => (
  <Field name={name} subscription={{ value: true }}>
    {({ input }) => roleLabel(input.value)}
  </Field>
);

/**
 * Ownership — REQUIRED on every block, guarantor or not.
 *
 * It leads the block because it is what the rest of the card is weighed by: the
 * owner-weighted FICO calculation and the ≥50%-owner check both read it, and the
 * ownership checks need the FULL cap table, which is why a non-guarantor owner
 * still has to declare a percentage (`rtl-guc-sizer-frontend.md` §2.3, §3).
 *
 * It no longer assumes 100% for a lone applicant. The assumption was only ever
 * safe while one applicant meant one owner, and the entity-member count on the
 * Borrowing Entity block exists precisely because that is often not true.
 */
const OwnershipField = ({ name, span }) => (
  <Field2 label="Entity Ownership %" required span={span}>
    <RffPercent name={name} factor={100} required placeholder="0" />
  </Field2>
);

/**
 * FICO, required only for a US Citizen.
 *
 * A foreign national is priced WITHOUT a domestic score — the engine derives
 * foreign-national status from `citizenshipStatus` and takes a different path —
 * so requiring a score there would block a scenario that prices perfectly well
 * without one. For a citizen the opposite holds: the class is credit points plus
 * experience points, and with no FICO the credit half silently resolves to
 * nothing.
 *
 * Reads its neighbour's value with a single-field subscription; the applicant
 * cards are a repeater, so a form-wide subscription here would re-render every
 * card on every keystroke in any of them.
 */
const FicoField = ({ name, citizenshipName }) => {
  const { input: citizenship } = useField(citizenshipName, {
    subscription: { value: true },
  });
  const required = citizenship.value === "US Citizen";
  return (
    <Field2
      label="FICO"
      required={required}
      /* A tooltip, not a hint: this note appears and disappears as citizenship
         changes, and a hint under one cell makes that cell taller than the two
         beside it — the row's shared baseline moves every time the neighbouring
         dropdown is touched. */
      tip={required ? undefined : "Not required for a foreign national."}
    >
      <RffNum name={name} placeholder="Score" required={required} />
    </Field2>
  );
};


/**
 * GUC with no ground-up track record.
 *
 * Ground-up classification is the two ground-up categories and nothing else, so
 * zero across both is not a low score — it is no basis for a class at all, and
 * the scenario prices ineligible. It is stated here, on the inputs, rather than
 * left for the result: the remedy is a structuring path (a GC completion
 * guarantee at reduced leverage), and the user should see it while they are
 * still in the experience fields.
 *
 * Sums across ALL applicants, matching the engine's cumulative-across-guarantors
 * rule.
 */
const GUC_EXPERIENCE_KEYS = [
  "ground_up_completed_and_sold",
  "ground_up_retained_as_rental",
];

const GucExperienceNote = () => {
  const { input: product } = useField("loanProductType", {
    subscription: { value: true },
  });
  const { input: borrowers } = useField("borrowers", {
    subscription: { value: true },
  });
  if (product.value !== "Ground Up Construction") return null;

  const rows = borrowers.value || [];
  /* GATED ON SOMETHING HAVING BEEN ENTERED. Blocks now push EMPTY, so "sum the
     experience and compare to zero" declared every fresh GUC scenario ineligible
     before the user had typed anything — a scarier claim than the form had any
     basis for. The banner is about a track record that was entered and came to
     nothing, not about a form nobody has filled in yet. */
  const anyEntered = rows.some((b) =>
    EXPERIENCE_CATEGORIES.some((c) => {
      const v = b?.experience?.[c.key];
      return v != null && v !== "";
    }),
  );
  if (!anyEntered) return null;

  const total = rows.reduce(
    (sum, b) =>
      sum +
      GUC_EXPERIENCE_KEYS.reduce(
        (n, k) => n + (Number(b?.experience?.[k]) || 0),
        0,
      ),
    0,
  );
  if (total > 0) return null;

  return (
    <Flx
      g={1}
      sx={{
        mb: 2,
        p: "9px 12px",
        borderRadius: SZ.radiusSm,
        background: SZ.badBg,
        border: `1px solid ${SZ.bad}33`,
      }}
    >
      <Txt sx={{ fontSize: 11.5, color: SZ.ink700 }}>
        <Box component="span" sx={{ fontWeight: 700, color: SZ.ink900 }}>
          No ground-up experience entered.
        </Box>{" "}
        A ground-up scenario with no completed ground-up projects prices as
        ineligible. The path is a General Contractor completion guarantee,
        subject to reduced overall leverage — request it as a structuring
        exception rather than repricing.
      </Txt>
    </Flx>
  );
};

/**
 * The standing caveat a declared flag triggers. It is guidance, not a gate —
 * nothing about the price changes, but the terms are explicitly conditional.
 */
const BackgroundFlagNote = ({ name }) => {
  const { input } = useField(name, { subscription: { value: true } });
  if (input.value !== true) return null;
  return (
    <Flx
      g={1}
      sx={{
        mt: 2,
        p: "9px 12px",
        borderRadius: SZ.radiusSm,
        background: SZ.warnBg,
        border: `1px solid ${SZ.warn}33`,
      }}
    >
      <Txt sx={{ fontSize: 11.5, color: SZ.ink700 }}>
        Priced terms and leverage are subject to due diligence on assets and
        guarantors — background flags may impact deal structure.
      </Txt>
    </Flx>
  );
};

/** A row counts as a guarantor unless it says otherwise — `newBorrower()` defaults to yes. */
const isGuarantorRow = (b) => b?.isGuarantor !== false;

const MAX_APPLICANTS = 20;

/**
 * The DECLARED guarantor count — the first numeric field in this section, and
 * the only count that lives here (`rtl-guc-sizer-frontend.md` §2.3).
 *
 * It AUTO-POPULATES BLOCKS, NEVER VALUES. Entering 3 pushes three EMPTY
 * guarantor blocks and that is the entire extent of it.
 *
 * PUSH-ONLY. Raising the count adds blocks; lowering it never deletes one.
 * A count is a claim about the deal, typed quickly and often wrong; the blocks
 * hold data somebody entered. Letting the number destroy them means a stray
 * keystroke silently drops an applicant, so a block only ever leaves by its own
 * remove button. Extra applicants beyond the declared count are kept for the
 * same reason.
 *
 * DISAGREEMENT IS FLAGGED, NOT BLOCKED: declared ≠ entered surfaces here for
 * reconciliation and pricing still runs — the backend records the same
 * disagreement as a `dataChecks[]` entry.
 */
const GuarantorCountField = ({ fields }) => {
  const { input: declared } = useField("numberOfGuarantors", {
    subscription: { value: true },
  });
  const { input: borrowers } = useField("borrowers", {
    subscription: { value: true },
  });
  const rows = borrowers.value || [];
  const entered = rows.filter(isGuarantorRow).length;
  const count = Number(declared.value) || 0;

  /* Applied against a REF of what this field last pushed for, not against the
     row count: pushing changes the row count, so comparing to it would re-run
     the effect on its own output. Entering 3 when three blocks already exist
     from an earlier 3 is a no-op either way. */
  const appliedRef = useRef(0);
  useEffect(() => {
    if (!count || count === appliedRef.current) return;
    const missing = count - entered;
    appliedRef.current = count;
    for (let i = 0; i < missing; i += 1) fields.push(newBorrower());
    // `entered`/`fields` are read at fire time, not depended on — either as a
    // dependency would re-run this against the blocks it just pushed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  const mismatch = count > 0 && count !== entered;

  return (
    <Field2
      label="# Guarantors (declared)"
      hint={
        mismatch
          ? `${entered} guarantor block${entered === 1 ? "" : "s"} entered — pricing uses the blocks below.`
          : "Opens a blank block per guarantor. Lowering it never removes one."
      }
    >
      <StepperInput
        value={declared.value === "" || declared.value == null ? "" : Number(declared.value)}
        onChange={declared.onChange}
        onBlur={declared.onBlur}
        min={0}
        max={MAX_APPLICANTS}
        placeholder="0"
        commitOnBlur
      />
    </Field2>
  );
};

export default function BorrowersSection({ first }) {
  return (
    <Section
      first={first}
      title="Guarantors & applicants"
      /* VERBATIM from the annotated review (rtl-guc-sizer-annotations.md §2.2).
         It reads as three instructions rather than a description because that is
         what it is — the desk's own wording for how to fill the section in. */
      subtitle="For LLCs detail all entity members. Enter percent ownership with majority owner first. Non-guarantor ownership percentages required for ownership checks."
    >
      <FieldArray name="borrowers">
        {({ fields }) => {
          return (
            <>
              <GucExperienceNote />

              {/* The count that builds the list, asked once for the deal rather
                  than per applicant. How many OWNERS the entity has is a
                  different question and lives on the Borrowing Entity block. */}
              <Grid cols={2} sx={{ mb: 3 }}>
                <GuarantorCountField fields={fields} />
              </Grid>

              {fields.map((f, index) => (
                <RepeatItem
                  key={f}
                  title={`Applicant ${index + 1}`}
                  sub={<RoleSub name={`${f}.isGuarantor`} />}
                  onRemove={() => fields.remove(index)}
                  removeLabel={`Remove applicant ${index + 1}`}
                  defaultCollapsed={index > 0}
                >
                  {/* OWNERSHIP + ROLE OPEN THE BLOCK. Ownership drives the
                      owner-weighted FICO calculation and the ≥50%-owner check,
                      so it leads; the guarantor toggle is the other structural
                      fact about this person. Everything below is credit. */}
                  <SubLabel first>Ownership &amp; role</SubLabel>
                  <Grid cols={12}>
                    <OwnershipField name={`${f}.entityPercentOwnership`} span={6} />
                    <Field2 label="Is a Guarantor" span={6}>
                      <RffSwitch name={`${f}.isGuarantor`} />
                    </Field2>
                  </Grid>

                  {/* Citizenship sits HERE, in credit, because that is what it
                      is: the pricing engine derives foreign-national status from
                      it (pricing_engine.py `price_and_screen`) and a foreign
                      national is priced without a domestic score, which is why
                      the FICO beside it is required only for a US Citizen. It
                      replaced a "Foreign national" Yes/No chip bound to
                      `isForeignNational`, a field on no backend model. */}
                  <SubLabel>Liquidity &amp; credit</SubLabel>
                  <Grid cols={4}>
                    <Field2 label="Citizenship status">
                      <RffSelect
                        name={`${f}.citizenshipStatus`}
                        options={selectOptionsCitizenshipStatus}
                      />
                    </Field2>
                    <FicoField name={`${f}.ficoMid`} citizenshipName={`${f}.citizenshipStatus`} />
                    <Field2 label="Cash liquidity">
                      <RffMoney name={`${f}.cashLiquidity`} placeholder="0" />
                    </Field2>
                    <Field2
                      label="Other liquidity"
                      tip="Money market, securities, life insurance cash value, IRA / 401k."
                    >
                      <RffMoney name={`${f}.otherLiquidity`} placeholder="0" />
                    </Field2>
                  </Grid>

                  <SubLabel note="last 3 years">Experience</SubLabel>
                  <Grid cols={3}>
                    {EXPERIENCE_CATEGORIES.map((c) => (
                      <Field2 key={c.key} label={c.label}>
                        <RffStepper name={`${f}.experience.${c.key}`} placeholder="0" min={0} />
                      </Field2>
                    ))}
                  </Grid>

                  {/* ONE toggle, not eleven named flags. At scenario stage this
                      is a self-declaration — "is there anything here?" — and a
                      Yes changes the CONVERSATION (diligence, deal structure)
                      rather than the price. The named `GuarantorBackgroundFlags`
                      booleans are collected in underwriting against the
                      background report. Scenario-only: `backgroundFlagsPresent`
                      is stripped from the Save Loan payload, and the LOS pricing
                      engine reads `sizerBackgroundFlags` — this declares, it
                      does not price. */}
                  <SubLabel>Background</SubLabel>
                  <Grid cols={4}>
                    <Field2 label="Any background flags">
                      <RffSwitch name={`${f}.backgroundFlagsPresent`} />
                    </Field2>
                  </Grid>
                  <BackgroundFlagNote name={`${f}.backgroundFlagsPresent`} />

                  {/* IDENTITY LAST, and optional. A name prices nothing — it is
                      needed when the scenario becomes a loan, not to quote one.
                      That is the point of the whole block order: a user reaches
                      a price without typing a single name. */}
                  <SubLabel note="optional until loan creation">Identity</SubLabel>
                  <Grid cols={2}>
                    <Field2 label="First name">
                      <RffText name={`${f}.firstName`} placeholder="First name" />
                    </Field2>
                    <Field2 label="Last name">
                      <RffText name={`${f}.lastName`} placeholder="Last name" />
                    </Field2>
                  </Grid>
                </RepeatItem>
              ))}

              {/* Extra applicants are MANUAL and optional — non-guarantor
                  parties the declared count says nothing about, so the block
                  this adds is seeded Guarantor: No. */}
              <AddButton onClick={() => fields.push(newBorrower({ isGuarantor: false }))}>
                Add applicant
              </AddButton>
            </>
          );
        }}
      </FieldArray>
    </Section>
  );
}
